import { Undo2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Notify } from "@/components/ui/notify";
import type { DataImportSummary } from "@/types/data-import.types";
import { ImportIssueList } from "../ImportIssueList";

interface ResultStepProps {
  imp: DataImportSummary;
  isReverting: boolean;
  onRevert: () => void;
  onReset: () => void;
}

function Row({ label, value }: { label: string; value: number | string }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wider text-muted-foreground">{label}</dt>
      <dd className="font-mono text-base font-semibold">{value}</dd>
    </div>
  );
}

/**
 * Spec 082 — o resultado da importação. Além dos totais, a lista de linhas com problema: nenhuma
 * linha do arquivo é descartada em silêncio, e o gestor precisa poder ir até a linha no Excel.
 */
export function ResultStep({ imp, isReverting, onRevert, onReset }: ResultStepProps) {
  const completed = imp.status === "COMPLETED";
  const reverted = imp.status === "REVERTED";

  return (
    <div className="space-y-3">
      <Notify.Banner
        variant={completed ? "success" : reverted ? "info" : "danger"}
        title={
          completed
            ? "Importação concluída."
            : reverted
              ? "Importação desfeita."
              : "Importação falhou."
        }
        description={
          reverted
            ? "As execuções que ela criou foram removidas. O que foi digitado na tela continua lá."
            : undefined
        }
      />

      <dl className="grid grid-cols-2 gap-1 text-sm md:grid-cols-4">
        <Row label="Total" value={imp.totalRows ?? "—"} />
        <Row label="Importadas" value={imp.validRows ?? "—"} />
        <Row label="Ignoradas" value={imp.ignoredRows ?? "—"} />
        <Row label="Rejeitadas" value={imp.errorRows ?? "—"} />
      </dl>

      <ImportIssueList issues={imp.errorsPreview} />

      <div className="flex flex-wrap gap-2">
        <Button onClick={onReset}>Nova importação</Button>
        {completed ? (
          <Button variant="outline" onClick={onRevert} disabled={isReverting}>
            <Undo2 size={14} className="mr-1" />
            Desfazer importação
          </Button>
        ) : null}
      </div>
    </div>
  );
}
